import { Injectable, inject } from '@angular/core';
import { AppStorageService } from './app-storage.service';
import { DeviceDiscoveryService, type DiscoveredDevice } from './device-discovery.service';
import { ZeroconfDiscoveryService } from './zeroconf-discovery.service';

const CACHED_MDNS_KEY = 'rf_cached_mdns_hostnames';

/**
 * Native LAN discovery for Capacitor (Android / iOS).
 *
 * Both platforms resolve `.local` mDNS names via the OS mDNS stack when the
 * app makes an outbound HTTP request, so we reuse the same probe logic as the
 * web service.  The extra step here is to seed the candidate list with any
 * hostnames cached from previous successful discoveries so the app reconnects
 * instantly without waiting for a full scan.
 */
@Injectable({ providedIn: 'root' })
export class NativeLanDiscoveryService {
  private readonly storage = inject(AppStorageService);
  private readonly webDiscovery = inject(DeviceDiscoveryService);
  private readonly zeroconf = inject(ZeroconfDiscoveryService);

  async discover(): Promise<DiscoveredDevice[]> {
    // Restore previously discovered hostnames into the candidate list so we
    // probe them first (fast reconnect path).
    const cached = await this.storage.get<string[]>(CACHED_MDNS_KEY, []);
    for (const hostname of cached) {
      this.webDiscovery.addCandidate(hostname);
    }

    // Browse mDNS (_http._tcp.local) and probe known candidates in parallel.
    // discover() finishes quickly when cached candidates respond; browse()
    // keeps listening for 4s and seeds new candidates.
    const [newFound, devices] = await Promise.all([
      this.zeroconf.browse(4000),
      this.webDiscovery.discover(),
    ]);

    // Second pass: if ZeroConf found candidates not in the initial list,
    // probe them now that browse has finished.
    const finalDevices = newFound > 0 ? await this.webDiscovery.discover() : devices;

    // Persist any newly discovered hostnames for the next session.
    const hostnames = finalDevices
      .map((d) => d.mdnsHostname)
      .filter((h): h is string => h !== null);

    if (hostnames.length > 0) {
      await this.storage.set(CACHED_MDNS_KEY, hostnames);
    }

    return finalDevices;
  }
}
